'use client';

/**
 * SuggestedAlongRoute — "Worth a stop on the way": a short list of places
 * near the middle of the trip's longer drives, each saying what it costs to
 * call in ("On your route", "4 km off route", "≈ +18 km"). "+" puts the place
 * at exactly that leg, the same as the "+" between two stops.
 *
 * Quiet by design: it renders nothing until it has at least one card with a
 * real photo, so a sparse stretch of road never shows an empty box.
 */

import { PlaceImage, placeKindFrom } from './PlaceTile';
import React, { useEffect, useMemo, useState } from 'react';
import { searchEntitiesByBounds } from '../../lib/searchEntities';
import type { ResolvedLocation } from '../LocationAutocomplete';
import { legBoxes, pickForLeg, type LegPoint, type RouteSuggestion } from './routeSuggest';
import { nearestOnPath, offRouteLabel, pointAlongRoute } from '../../utils/routeSnap';
import { formatAddedKm, placementInfo, withRoadDistance } from '../../utils/tripInsert';
import type { TripLeg, TripStop } from '../../types/trip';
import { dayHex } from './roles';

/**
 * The day each leg is driven on (by insert index: the leg into stop i is
 * driven on the day you reach stop i). Day 1 at the start, +1 per night.
 */
export function legDay(stops: Pick<TripStop, 'nights'>[], insertAt: number): number {
  let day = 1;
  for (let i = 1; i <= insertAt && i < stops.length; i++) day += Math.max(0, stops[i - 1].nights ?? 0);
  return day;
}

/**
 * Ideas grouped the way the itinerary is: by day, each day once, with its
 * legs under it in order. A day with one leg reads "Day 1 · A → B"; a day
 * of two legs (a pass-through in the middle) reads "Day 2" once, then each
 * leg as a sub-heading, never "Day 2" twice.
 */
export function groupIdeasByDay<T extends { insertAt: number }>(
  stops: Pick<TripStop, 'nights'>[],
  legs: T[]
): { day: number; legs: T[] }[] {
  const out: { day: number; legs: T[] }[] = [];
  [...legs]
    .sort((a, b) => a.insertAt - b.insertAt)
    .forEach((leg) => {
      const day = legDay(stops, leg.insertAt);
      const g = out.find((x) => x.day === day);
      if (g) g.legs.push(leg);
      else out.push({ day, legs: [leg] });
    });
  return out;
}

/**
 * The whole day's drive: where day `day` starts and ends, and how many legs
 * it drives (a leg into stop i is driven on legDay(stops, i)).
 */
export function dayEnds(
  stops: Pick<TripStop, 'nights' | 'name'>[],
  day: number
): { from: string; to: string; legs: number } | null {
  let first = -1;
  let last = -1;
  for (let i = 1; i < stops.length; i++) {
    if (legDay(stops, i) !== day) continue;
    if (first < 0) first = i;
    last = i;
  }
  if (first < 0) return null;
  return { from: stops[first - 1].name, to: stops[last].name, legs: last - first + 1 };
}

/** With a road line, ideas further off it than this aren't on the way. */
const MAX_IDEA_OFF_ROUTE_KM = 15;
/**
 * "On the way" means a short call-in. Past this many extra km by road an
 * idea is a side trip, not a stop on the way, so it isn't offered here.
 */
export const MAX_ON_THE_WAY_KM = 35;

/** The detour line under an idea: "On your route" or "+12 km detour". */
export function detourLine(info: { onRoute?: boolean; addKm?: number } | null): string | null {
  if (!info) return null;
  if (info.onRoute) return 'On your route';
  if (typeof info.addKm !== 'number' || !Number.isFinite(info.addKm)) return null;
  return `${formatAddedKm(info.addKm)} detour`;
}

interface Props {
  stops: TripStop[];
  /** Routed legs, so "+12 km" is learned from the real road. */
  legs?: TripLeg[];
  onAdd: (place: ResolvedLocation, insertAt: number) => void;
  /** The drawn road route, for an exact "N km off route". */
  route?: { lat: number; lng: number }[] | null;
}

/** What calling in costs, measured off the road line ("On your route",
 *  "4 km off route"). Without a road line there's no honest figure: a
 *  straight-line detour called Tawonga "on the way" from Melbourne to
 *  Sydney, a 100 km mountain detour by road. So it says nothing. */
export function detourNote(
  c: Pick<RouteSuggestion, 'lat' | 'lng' | 'insertAt'>,
  _stops: LegPoint[],
  route?: { lat: number; lng: number }[] | null
): string | null {
  return offRouteLabel(route, c);
}

/** The card's photo over a drawn tile for its kind: never a grey hole. */
function SuggestionPhoto({ src, detail }: { src: string; detail: string }) {
  return <PlaceImage src={src || null} kind={placeKindFrom(detail)} className="h-12 w-16 flex-none rounded-tp-sm" />;
}

export default function SuggestedAlongRoute({ stops, legs: routedLegs = [], onAdd, route }: Props) {
  // Once the road line is known, look along the ROAD (a coast road and the
  // straight line between its ends can be 100 km apart), and keep only ideas
  // close to it.
  const routeKey =
    route && route.length >= 2
      ? `${route.length}:${route[0].lat.toFixed(3)},${route[route.length - 1].lng.toFixed(3)}`
      : '';
  const legs = useMemo(
    () =>
      legBoxes(
        stops,
        4,
        routeKey
          ? (i, t) => {
              const a = stops[i];
              const b = stops[i + 1];
              if (typeof a?.lat !== 'number' || typeof a?.lng !== 'number' || typeof b?.lat !== 'number' || typeof b?.lng !== 'number') {
                return null;
              }
              return pointAlongRoute(route, { lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }, t);
            }
          : undefined
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stops, routeKey]
  );
  // Refetch only when the legs move, not on every render of the planner.
  const legsKey = legs.map((l) => `${l.insertAt}:${l.box.north.toFixed(2)},${l.box.west.toFixed(2)}`).join('|');
  const onTripKey = stops.map((s) => s.entityId ?? '').join(',');
  const [cards, setCards] = useState<RouteSuggestion[]>([]);

  useEffect(() => {
    if (!legs.length) {
      setCards([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const onTrip = new Set(stops.map((s) => s.entityId).filter((n): n is number => typeof n === 'number'));
      const taken = new Set<number>();
      const results = await Promise.all(
        legs.map((leg) =>
          searchEntitiesByBounds({ bounds: leg.box, zoom: 10, limit: 150 })
            .then((r) => r.entities ?? [])
            .catch(() => [])
        )
      );
      if (cancelled) return;
      const picked: RouteSuggestion[] = [];
      legs.forEach((leg, i) => {
        const card = pickForLeg(
          results[i],
          leg,
          onTrip,
          taken,
          routeKey ? (pt) => (nearestOnPath(route!, pt)?.km ?? Infinity) <= MAX_IDEA_OFF_ROUTE_KM : undefined
        );
        if (card) {
          taken.add(card.entityId);
          picked.push(card);
        }
      });
      setCards(picked);
    }, 700);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legsKey, onTripKey, routeKey]);

  // What each idea costs, by road once the legs are routed. Side trips past
  // MAX_ON_THE_WAY_KM drop out; a leg left with no idea drops its heading.
  const scored = cards
    .map((c) => {
      const offKm = route && route.length >= 2 ? nearestOnPath(route, c)?.km : null;
      const info = withRoadDistance(placementInfo(stops, { lat: c.lat, lng: c.lng }, routedLegs, c.insertAt), offKm);
      return { c, info };
    })
    .filter(({ info }) => !info || info.onRoute || !(info.addKm > MAX_ON_THE_WAY_KM));
  if (!scored.length) return null;

  // Cards follow the trip: grouped under the drive they sit on, in leg
  // order, and re-dealt (with a short rise) whenever the legs change.
  const groups: { insertAt: number; fromName: string; toName: string; cards: typeof scored }[] = [];
  for (const x of scored) {
    const g = groups.find((y) => y.insertAt === x.c.insertAt);
    if (g) g.cards.push(x);
    else groups.push({ insertAt: x.c.insertAt, fromName: x.c.fromName, toName: x.c.toName, cards: [x] });
  }

  // Trips of two or more days: each leg heading leads with its day, in the
  // day's colour, so an idea never reads as belonging to the day above it.
  const multiDay = legDay(stops, stops.length - 1) > 1;

  return (
    <section
      className="mt-5 border-0 border-t border-solid border-tp-line px-1 pt-4"
      data-testid="suggested-along-route"
      aria-labelledby="route-sugg-title"
      key={legsKey}
    >
      <h4 id="route-sugg-title" className="m-0 text-tp-base font-semibold text-tp-ink" data-sheet-row>
        Worth a stop on the way
      </h4>
      {(multiDay ? groupIdeasByDay(stops, groups) : [{ day: 1, legs: groups }]).map((d) => {
        // One format for every day: "Day N · From → To" (the whole day's
        // drive), then, on a day of two or more legs, each leg with ideas on
        // its own line. Headings, leg lines and cards share one left edge;
        // the day colour bar sits in the gutter.
        const ends = multiDay ? dayEnds(stops, d.day) : null;
        const legLines = !multiDay || (ends?.legs ?? 1) > 1;
        return (
          <div key={`day-${d.day}`} className="mt-3" data-testid="route-sugg-day">
            {multiDay && ends && (
              <p className="relative m-0 flex min-w-0 items-center gap-1.5 pl-1 text-tp-sm font-semibold text-tp-ink" data-sheet-row data-testid="route-sugg-day-head">
                <span aria-hidden="true" className="absolute -left-2 h-3.5 w-1 rounded-full" style={{ background: dayHex(d.day) }} />
                <span className="flex-none">Day {d.day}</span>
                <span aria-hidden="true" className="flex-none font-normal text-tp-faint">·</span>
                <LegWords from={ends.from} to={ends.to} />
              </p>
            )}
            {d.legs.map((g) => (
              <div key={`${g.insertAt}:${g.fromName}`}>
                {legLines && (
                  <p
                    className={`m-0 flex min-w-0 items-center gap-1.5 pl-1 text-tp-sm font-medium text-[#4a5a73] ${multiDay ? 'mt-1' : ''}`}
                    data-testid="route-sugg-leg"
                    data-sheet-row
                  >
                    <LegWords from={g.fromName} to={g.toName} />
                  </p>
                )}
                <ul className="m-0 mt-0.5 flex list-none flex-col p-0">
                  {g.cards.map(({ c, info }) => {
                    const onRoute = !!info?.onRoute;
                    const detour = detourLine(info);
                    return (
                      <li key={c.key} className="motion-safe:animate-[tp-rise_200ms_ease-out]" data-sheet-row>
                        <button
                          type="button"
                          onClick={() =>
                            onAdd({ label: c.name, lat: c.lat, lng: c.lng, entityId: c.entityId, kind: 'campsite' }, c.insertAt)
                          }
                          aria-label={`Add ${c.name} between ${c.fromName} and ${c.toName}${detour ? `, ${detour.toLowerCase()}` : ''}`}
                          className="group/sugg flex min-h-[64px] w-full min-w-0 items-center gap-3 rounded-tp-sm border-0 bg-transparent px-1 py-1.5 text-left transition-colors duration-150 [@media(hover:hover)]:hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                        >
                          <SuggestionPhoto src={c.image} detail={c.detail} />
                          {/* Name, then what it is, then what it costs: each on
                              its own line, so the detour figures line up down
                              the list and can be scanned. */}
                          <span className="min-w-0 flex-1">
                            <span className="line-clamp-2 block text-tp-base font-medium leading-[19px] text-tp-ink">{c.name}</span>
                            <span className="mt-0.5 block truncate text-tp-sm leading-[18px] text-[#4a5a73]">{c.detail}</span>
                            {detour && (
                              // One quiet slate for every figure: green is kept
                              // for "Free" alone.
                              <span
                                className="mt-0.5 flex items-center gap-1 whitespace-nowrap text-tp-sm font-medium leading-[18px] tabular-nums text-[#3a4a63]"
                                data-testid="route-sugg-detour"
                                data-on-route={onRoute ? 'true' : undefined}
                              >
                                {onRoute ? <IconOnRoute /> : <IconDetour />}
                                {detour}
                              </span>
                            )}
                          </span>
                          {/* Same "+" as the seams: outlined at rest, filled only
                              while a mouse is over the row. */}
                          <span
                            aria-hidden="true"
                            className="flex h-8 w-8 flex-none items-center justify-center rounded-full border-[1.5px] border-solid border-tp-brand bg-white text-tp-brand transition-colors duration-150 [@media(hover:hover)]:group-hover/sugg:bg-tp-brand [@media(hover:hover)]:group-hover/sugg:text-white"
                          >
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-3.5 w-3.5">
                              <path d="M8 3.5v9M3.5 8h9" />
                            </svg>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        );
      })}
    </section>
  );
}

/** "From → To", each name truncating on its own. */
function LegWords({ from, to }: { from: string; to: string }) {
  return (
    <>
      <span className="min-w-0 truncate">{from}</span>
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 flex-none" aria-hidden="true">
        <path d="M3 8h10M9.5 4.5 13 8l-3.5 3.5" />
      </svg>
      <span className="min-w-0 truncate">{to}</span>
    </>
  );
}

/** On the line itself: a dot on a road. */
function IconOnRoute() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 flex-none text-tp-faint" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M2 8h12" />
      <circle cx="8" cy="8" r="2.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** A side trip off the road and back. */
function IconDetour() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 flex-none text-tp-faint" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12h3c0-5 6-5 6 0h3" />
    </svg>
  );
}
